import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { attributionPillSx, textLinkSx } from './CaseStudies';
import CaseStudyNextSteps from '../components/CaseStudyNextSteps';
import { cardSx } from '../components/stitch/Section';
import { caseStudyPath } from '../data/CaseStudies';
import { InsightArticle, insightPath, insights } from '../data/Insights';
import { tokens } from '../theme';

// Built to the Stitch "Insight Article" screen (project 1587770953174176333): header, a sticky
// in-page list beside a 720px reading column, a related-work card and more articles. The
// screen's "Architecture Note" and rewritten body copy are left out; the article text is ours.
const tocLinkSx = {
  display: 'block',
  py: 0.75,
  color: tokens.onSurfaceVariant,
  fontSize: '0.875rem',
  lineHeight: 20 / 14,
  fontWeight: 500,
  textDecoration: 'none',
  '&:hover': { color: tokens.primary, textDecoration: 'underline' },
} as const;

function InsightArticlePage({ article }: { article: InsightArticle }) {
  const others = insights.filter((item) => item.id !== article.id);
  const sectionId = (index: number) => `${article.id}-section-${index + 1}`;
  const relatedId = `${article.id}-related-work`;
  return (
    <Container sx={{ '& .MuiTypography-root': { overflowWrap: 'anywhere' } }}>
      <Box sx={{ pt: { xs: 3, md: 4 } }}>
        <Box
          component={Link}
          to='/insights'
          sx={textLinkSx}
        >
          <ArrowBackIcon
            aria-hidden='true'
            sx={{ fontSize: 18 }}
          />
          All insights
        </Box>
      </Box>

      <Box
        component='article'
        aria-labelledby={`${article.id}-title`}
        sx={{ minWidth: 0 }}
      >
        <Box sx={{ py: { xs: 4, md: 7 }, maxWidth: '60rem' }}>
          <Typography
            variant='labelCaps'
            component='p'
            sx={attributionPillSx}
          >
            {article.topic}
          </Typography>
          <Typography
            component='h1'
            id={`${article.id}-title`}
            variant='h1'
            aria-label={`${article.title} — ${article.topic}`}
            sx={{ mt: 2.5 }}
          >
            {article.title}
          </Typography>
          <Typography
            variant='bodyLg'
            component='p'
            sx={{ mt: 3, maxWidth: '46rem' }}
          >
            {article.summary}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '16rem minmax(0, 45rem)' },
            gap: { xs: 4, lg: 8 },
            alignItems: 'start',
          }}
        >
          <Box
            component='nav'
            aria-labelledby={`${article.id}-contents`}
            sx={{
              ...cardSx,
              p: 3,
              display: { xs: 'none', lg: 'block' },
              position: 'sticky',
              top: '96px',
            }}
          >
            <Typography
              id={`${article.id}-contents`}
              component='h2'
              variant='labelCaps'
              sx={{ display: 'block', color: tokens.accentText, mb: 1 }}
            >
              On this page
            </Typography>
            <Box
              component='ul'
              sx={{ listStyle: 'none', m: 0, p: 0 }}
            >
              {article.sections.map((section, index) => (
                <li key={section.heading}>
                  <Box
                    component='a'
                    href={`#${sectionId(index)}`}
                    sx={tocLinkSx}
                  >
                    {section.heading}
                  </Box>
                </li>
              ))}
              <li>
                <Box
                  component='a'
                  href={`#${relatedId}`}
                  sx={tocLinkSx}
                >
                  Related delivered work
                </Box>
              </li>
            </Box>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            {article.sections.map((section, index) => (
              <Box
                component='section'
                key={section.heading}
                aria-labelledby={sectionId(index)}
                sx={{ mt: index === 0 ? 0 : { xs: 5, md: 6 } }}
              >
                <Typography
                  component='h2'
                  id={sectionId(index)}
                  variant='h3'
                  sx={{ scrollMarginTop: '96px' }}
                >
                  {section.heading}
                </Typography>
                {section.paragraphs.map((paragraph) => (
                  <Typography
                    key={paragraph}
                    variant='body1'
                    sx={{ mt: 2, fontSize: '1rem', lineHeight: 1.7 }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            ))}

            <Box
              component='section'
              aria-labelledby={relatedId}
              sx={{ ...cardSx, mt: { xs: 5, md: 6 }, p: { xs: 3, md: 4 } }}
            >
              <Typography
                variant='labelCaps'
                component='p'
                sx={{ color: tokens.accentText }}
              >
                Delivered experience
              </Typography>
              <Typography
                component='h2'
                id={relatedId}
                variant='h4'
                sx={{ mt: 1, scrollMarginTop: '96px' }}
              >
                Related delivered work
              </Typography>
              <Box
                component='ul'
                sx={{ listStyle: 'none', m: 0, mt: 2, p: 0, display: 'grid', gap: 1.5 }}
              >
                {article.relatedCaseStudies.map((study) => (
                  <Box
                    component='li'
                    key={study.studyId}
                    sx={{ p: 2, borderRadius: 2, bgcolor: tokens.surfaceLow }}
                  >
                    <Box
                      component={Link}
                      to={caseStudyPath(study.studyId)}
                      sx={{ ...textLinkSx, color: tokens.primary, fontSize: '1rem' }}
                    >
                      {study.title}
                    </Box>
                    <Typography
                      variant='bodySm'
                      component='p'
                      sx={{ mt: 0.5, color: tokens.onSurfaceVariant }}
                    >
                      ({study.attribution})
                    </Typography>
                  </Box>
                ))}
                {article.relatedPage && (
                  <li>
                    <Box
                      component={Link}
                      to={article.relatedPage.path}
                      sx={textLinkSx}
                    >
                      {article.relatedPage.label}
                      <ArrowForwardIcon
                        aria-hidden='true'
                        sx={{ fontSize: 18 }}
                      />
                    </Box>
                  </li>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component='nav'
        aria-labelledby='more-insights'
        sx={{
          mt: { xs: 6, md: 8 },
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          bgcolor: tokens.surfaceLow,
        }}
      >
        <Typography
          id='more-insights'
          component='h2'
          variant='h3'
        >
          More insights
        </Typography>
        <Box
          component='ul'
          sx={{
            listStyle: 'none',
            m: 0,
            mt: 3,
            p: 0,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {others.map((item) => (
            <Box
              component='li'
              key={item.id}
              sx={{ minWidth: 0 }}
            >
              <Box
                component={Link}
                to={insightPath(item.id)}
                sx={{
                  ...cardSx,
                  height: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 3,
                  color: tokens.primary,
                  fontFamily: '"Manrope Variable", sans-serif',
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  lineHeight: 26 / 18,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline', borderColor: tokens.secondary },
                }}
              >
                <Box
                  component='span'
                  sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                >
                  {item.title}
                </Box>
                <ArrowForwardIcon
                  aria-hidden='true'
                  sx={{ color: tokens.outline, flexShrink: 0 }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <CaseStudyNextSteps />
    </Container>
  );
}

export default InsightArticlePage;
